import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../../lib/api";
import { TID } from "../../lib/testIds";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const EMPTY = {
  id: "", name: "",
  executor: "playwright_web",
  web_service_id: "",
  browser_profile: "",
  provider: "",
  target_url: "",
  prompt_selector: "",
  submit_selector: "",
  response_selector: "",
  ready_selector: "",
  active: true,
  capabilities: ["chat"],
  notes: "",
};

// Mirrors `WEB_TARGET_CAPABILITIES` in backend/routers/gateway.py. The
// backend validates against its own list so this array only drives UI
// labels — adding one here without adding it there will yield 422.
const CAPABILITY_OPTIONS = [
  { id: "chat",              label: "Texto",            hint: "Chat / completions" },
  { id: "vision",            label: "Visión",           hint: "Entiende imágenes de entrada" },
  { id: "image_edit",        label: "Editar imagen",    hint: "Modifica una imagen enviada" },
  { id: "image_generation",  label: "Generar imagen",   hint: "Crea imágenes desde texto" },
  { id: "video_generation",  label: "Generar vídeo",    hint: "Experimental · requiere adaptador" },
];

export default function AddWebTargetDialog({ target, onClose, onSaved }) {
  const isEdit = !!target;
  const [form, setForm] = useState(EMPTY);
  const [webServices, setWebServices] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(target ? { ...EMPTY, ...target, capabilities: Array.isArray(target.capabilities) && target.capabilities.length ? target.capabilities : ["chat"] } : EMPTY);
    // Load references (read-only — no mutation of other modules).
    (async () => {
      try {
        const [ws, pr] = await Promise.all([
          api.get("/web-services"),
          api.get("/browser/profiles"),
        ]);
        setWebServices(ws.data || []);
        setProfiles(pr.data || []);
      } catch (e) {
        // Non-blocking: user can still type profile name manually
      }
    })();
  }, [target]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleCapability = (capId) => {
    setForm((f) => {
      const has = (f.capabilities || []).includes(capId);
      // `chat` is the baseline — operators shouldn't be able to end up
      // with an empty capabilities list (would yield an unusable model
      // entry in `/v1/models`). We silently keep at least one on.
      const next = has
        ? (f.capabilities || []).filter((c) => c !== capId)
        : [...(f.capabilities || []), capId];
      return { ...f, capabilities: next.length ? next : ["chat"] };
    });
  };

  const submit = async () => {
    if (!isEdit && (!form.id.trim() || !form.name.trim() || !form.browser_profile.trim()
        || !form.target_url.trim() || !form.prompt_selector.trim() || !form.response_selector.trim())) {
      toast.error("Rellena id, nombre, perfil, URL, prompt_selector y response_selector.");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const { id, created_at, updated_at, ...patch } = form;
        await api.patch(`/gateway/web-targets/${target.id}`, patch);
        toast.success("Web target actualizado");
      } else {
        await api.post("/gateway/web-targets", { ...form, id: form.id.trim() });
        toast.success("Web target creado");
      }
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid={TID.gateway.webTargetDialog}
        className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? `Editar web target "${target.id}"` : "Nuevo Web Target"}</DialogTitle>
        </DialogHeader>

        <div className="text-xs text-zinc-500">
          Los Web Targets orquestan sesiones web existentes en KRAI CONNECT.
          No duplican perfiles ni sesiones — solo referencian los ya creados.
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">ID interno</Label>
              <Input
                data-testid={TID.gateway.webTargetField("id")}
                value={form.id}
                onChange={(e) => set({ id: e.target.value.toLowerCase() })}
                disabled={isEdit}
                placeholder="gemini-web"
                className="bg-zinc-900 border-zinc-800 font-mono text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Nombre</Label>
              <Input
                data-testid={TID.gateway.webTargetField("name")}
                value={form.name}
                onChange={(e) => set({ name: e.target.value })}
                placeholder="Gemini Web"
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Servicio web (referencia)</Label>
              <Select value={form.web_service_id || "__none__"}
                      onValueChange={(v) => set({ web_service_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid={TID.gateway.webTargetField("web_service_id")}
                               className="bg-zinc-900 border-zinc-800">
                  <SelectValue placeholder="Ninguno" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="__none__">Ninguno</SelectItem>
                  {webServices.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Perfil navegador *</Label>
              <Select value={form.browser_profile || undefined}
                      onValueChange={(v) => {
                        // If provider is empty or was auto-following the old
                        // profile, keep it in sync with the new profile.
                        const followed = !form.provider || form.provider === form.browser_profile;
                        set({ browser_profile: v, ...(followed ? { provider: v } : {}) });
                      }}>
                <SelectTrigger data-testid={TID.gateway.webTargetField("browser_profile")}
                               className="bg-zinc-900 border-zinc-800">
                  <SelectValue placeholder="Elige perfil…" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {profiles.map((p) => (
                    <SelectItem key={p.name} value={p.name}>
                      {p.name}{p.has_storage_state ? "" : " (sin storage_state)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">
              Provider (KRAI API /generate)
              <span className="ml-2 text-[10px] text-zinc-600">
                opcional · si se deja vacío se usa el perfil
              </span>
            </Label>
            <Input
              data-testid={TID.gateway.webTargetField("provider")}
              value={form.provider}
              onChange={(e) => set({ provider: e.target.value.toLowerCase() })}
              placeholder={form.browser_profile || "gemini"}
              className="bg-zinc-900 border-zinc-800 font-mono text-sm"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">URL objetivo *</Label>
            <Input
              data-testid={TID.gateway.webTargetField("target_url")}
              value={form.target_url}
              onChange={(e) => set({ target_url: e.target.value })}
              placeholder="https://gemini.google.com/app"
              className="bg-zinc-900 border-zinc-800 font-mono text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Prompt selector *</Label>
              <Input
                data-testid={TID.gateway.webTargetField("prompt_selector")}
                value={form.prompt_selector}
                onChange={(e) => set({ prompt_selector: e.target.value })}
                placeholder="textarea, [contenteditable='true']"
                className="bg-zinc-900 border-zinc-800 font-mono text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Submit selector</Label>
              <Input
                value={form.submit_selector}
                onChange={(e) => set({ submit_selector: e.target.value })}
                placeholder="button[aria-label='Enviar']"
                className="bg-zinc-900 border-zinc-800 font-mono text-xs"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Response selector *</Label>
              <Input
                data-testid={TID.gateway.webTargetField("response_selector")}
                value={form.response_selector}
                onChange={(e) => set({ response_selector: e.target.value })}
                placeholder="[data-response-body]"
                className="bg-zinc-900 border-zinc-800 font-mono text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Ready selector</Label>
              <Input
                value={form.ready_selector}
                onChange={(e) => set({ ready_selector: e.target.value })}
                placeholder="textarea"
                className="bg-zinc-900 border-zinc-800 font-mono text-xs"
              />
            </div>
          </div>

          <div className="space-y-2 rounded border border-zinc-800/70 bg-black/30 p-3">
            <div className="flex items-baseline justify-between">
              <Label className="text-xs text-zinc-300">Capacidades expuestas</Label>
              <span className="text-[10px] text-zinc-600">
                Lo que verán las apps externas al llamar <code className="font-mono">/v1/models</code>
              </span>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-2 sm:grid-cols-3">
              {CAPABILITY_OPTIONS.map((c) => {
                const checked = (form.capabilities || []).includes(c.id);
                return (
                  <label
                    key={c.id}
                    data-testid={TID.gateway.webTargetField(`cap-${c.id}`)}
                    className={
                      "flex items-start gap-2 rounded px-2 py-1.5 cursor-pointer transition " +
                      (checked
                        ? "bg-emerald-500/10 border border-emerald-500/30"
                        : "bg-zinc-900/40 border border-zinc-800 hover:border-zinc-700")
                    }
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCapability(c.id)}
                      className="mt-0.5 accent-emerald-500"
                    />
                    <div className="min-w-0">
                      <div className="text-[12px] text-zinc-100 leading-tight">{c.label}</div>
                      <div className="text-[10px] text-zinc-500 leading-tight">{c.hint}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs text-zinc-400">Activo (administrativo)</Label>
            <Switch checked={form.active} onCheckedChange={(v) => set({ active: v })} />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">Notas</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => set({ notes: e.target.value })}
              rows={2}
              className="bg-zinc-900 border-zinc-800 text-sm resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancelar</Button>
          <Button
            data-testid={TID.gateway.webTargetSubmit}
            onClick={submit}
            disabled={saving}
            size="sm"
            className="bg-white text-black hover:bg-zinc-200"
          >
            {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear web target"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
